import { Node, Edge } from "@xyflow/react"
import { Position } from "@xyflow/react"
import { SystemViewData, NodeType, SystemNode, LoadPrecedenceEntry, SystemNodeAttributes } from "../types/SystemView"
import { AssetType } from "../components/nodes/Asset"
import { RefObject } from "react"
import ApiAssetNode from "../components/nodes/ApiAsset"
import { endOfDay, nowDateTime, startOfDay, toUserTZ } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import { getNodeColors, SystemNodeVersion } from "../types/SystemNode"
import { getNodeDimensions } from "../types/SystemNode"
import { AssetStatus } from "@hydrologiq/gql-experience/graphql"

// Map of uppercased enum values to their original AssetStatus enum value for case-insensitive lookup.
// The backend returns lowercase (e.g. "available", "depot_checked") and the enum may use
// uppercase (e.g. "AVAILABLE") or lowercase depending on the generated GraphQL types version.
const ASSET_STATUS_MAP = new Map<string, AssetStatus>(
  Object.values(AssetStatus).map((v) => [v.toUpperCase(), v as AssetStatus]),
)

/** How many Load Precedence rows a node shows (one per AC Load or Covered Connection). */
/**
 * A node's Load Precedence entries: its own single entry shows as its Power attribute,
 * the rest (a group's Modules, or an Asset with no Power attribute) as rows under it.
 */
export const splitLoadPrecedence = (
  entries: LoadPrecedenceEntry[],
  assetId: string | undefined,
  attributes: SystemNodeAttributes[] | undefined,
) => {
  const own = entries.filter((entry) => entry.assetId === assetId)
  const power = own.length === 1 && attributes?.some((a) => a.field === "power") ? own[0] : undefined
  return { power, rows: entries.filter((entry) => entry !== power) }
}

/** Rows a node adds under its attributes for Load Precedence. */
export const loadPrecedenceRowCount = ({ loadPrecedence, asset, attributes }: Pick<SystemNode, "loadPrecedence" | "asset" | "attributes">): number =>
  loadPrecedence?.entries.length ? splitLoadPrecedence(loadPrecedence.entries, asset?.id, attributes).rows.length : 0

/** Lines a node makes room for under its title: one per filled attribute slot (at least two, the height nodes have always had), then its Load Precedence rows. */
export const nodeRowCount = (node: Pick<SystemNode, "loadPrecedence" | "asset" | "attributes">): number =>
  Math.max(2, new Set(node.attributes?.map((a) => a.slot).filter((slot) => typeof slot === "number")).size) + loadPrecedenceRowCount(node)

/** The System view without Load Precedence, for tenants without the `ac_load` feature. */
export const withoutLoadPrecedence = (systemView: SystemViewData): SystemViewData => {
  const strip = (node: SystemNode): SystemNode => (node.loadPrecedence ? { ...node, loadPrecedence: undefined } : node)
  return {
    ...systemView,
    installations: systemView.installations.map((group) => ({ ...group, nodes: group.nodes.map(strip) })),
    isolatedNodes: systemView.isolatedNodes.map(strip),
  }
}

/** Convert a backend status string to the AssetStatus enum, or null if no match.
 *  Performs case-insensitive matching so it works regardless of whether the enum
 *  uses uppercase or lowercase string values. */
export const toAssetStatus = (status: string | null | undefined): AssetStatus | null => {
  if (!status) return null
  return ASSET_STATUS_MAP.get(status.toUpperCase()) ?? null
}

// Map API node types to our AssetType enum
export const NODE_TYPE_TO_ASSET_TYPE: Record<NodeType, AssetType> = {
  hydrogen_producer: AssetType.Producer,
  storage_hub: AssetType.Storage,
  refueling_bowser: AssetType.Storage,
  excavator: AssetType.Consumer,
  generator: AssetType.Generator,
  monitoring_device: AssetType.Consumer,
  asset_node: AssetType.Consumer, // Default, could be determined by asset.kind
  grid_connection: AssetType.Generator, // Added missing key
  solar_generation: AssetType.Generator, // Added missing key
  battery: AssetType.Generator,
  inverter: AssetType.Generator,
  electrolyser: AssetType.Producer,
}

// Helper to get status from either assignment object or assignmentStatus string
const getAssignmentStatus = (node: SystemNode): "active" | "inactive" | null => {
  // If the node has an assignment, use its status directly (most reliable source)
  if (node.assignment) {
    return node.assignment.status
  }
  // For non-controllable nodes without an assignment, derive from operational status
  if (!node.controllable && node.operationalStatus !== "unknown") {
    return node.operationalStatus === "active" ? "active" : "inactive"
  }
  // Backward compatibility fallback
  return node.assignmentStatus || null
}

// Map assignment status to styling
export const getNodeStyle = (
  assignmentStatus: "active" | "inactive" | null,
  nodeVersion: SystemNodeVersion,
  alertsEnabled?: boolean,
  alertSeverity?: "critical" | "warning" | "info" | null,
) => {
  const baseStyle = {
    height: "50px",
    justifyContent: "flex-start" as const,
    alignItems: "flex-start" as const,
    paddingLeft: "25px",
  }

  return {
    ...getNodeColors(nodeVersion, assignmentStatus, alertsEnabled, alertSeverity),
    ...baseStyle,
  }
}

// Helper to calculate group bounds for an installation
const calculateGroupBounds = (
  nodes: SystemNode[],
  nodeVersion: SystemNodeVersion,
  padding = 25,
  graphsEnabled?: boolean,
) => {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 300, height: 200 }
  }

  const rows = Math.max(0, ...nodes.map(nodeRowCount))
  const { width, height } = getNodeDimensions(nodeVersion, true, graphsEnabled, rows)

  // Find the bounds of the nodes
  const positions = nodes.map((node) => node.position)
  const minX = Math.min(...positions.map((p) => p.x))
  const maxX = Math.max(...positions.map((p) => p.x))
  const minY = Math.min(...positions.map((p) => p.y))
  const maxY = Math.max(...positions.map((p) => p.y))

  const headerHeight = 50

  // Calculate group size based on content with generous padding
  const contentWidth = maxX - minX + width
  const contentHeight = maxY - minY + height

  const groupWidth = contentWidth + padding * 2
  const groupHeight = contentHeight + padding * 2 + headerHeight

  return {
    x: minX,
    y: minY,
    width: groupWidth,
    height: groupHeight,
  }
}



// Transform API SystemNode to React Flow Node
export const transformSystemNodeToReactFlowNode = (
  systemNode: SystemNode,
  installationId?: string,
  installationStartDate?: DateTime,
  installationEndDate?: DateTime,
  groupBounds?: { x: number; y: number; width: number; height: number },
  connections?: SystemViewData["connections"],
  allNodes?: SystemNode[],
  container?: RefObject<HTMLElement | null>,
  nodeVersion: SystemNodeVersion = SystemNodeVersion.V1,
  graphsEnabled?: boolean,
  alertsEnabled?: boolean,
): Node => {
  const assetType = NODE_TYPE_TO_ASSET_TYPE[systemNode.type]
  const alertSeverity = systemNode.alerts?.highestSeverity
  const style = getNodeStyle(getAssignmentStatus(systemNode), nodeVersion, alertsEnabled, alertSeverity)

  // For grouped nodes, use position relative to group with simple offset
  let position = systemNode.position

  if (groupBounds) {
    // Simple relative positioning: node position minus group position plus padding
    position = {
      x: systemNode.position.x - groupBounds.x + 25, // 20px padding from left
      y: systemNode.position.y - groupBounds.y + 50, // 50px for header
    }
  }

  const { width, height } = getNodeDimensions(
    nodeVersion,
    systemNode.assignment || systemNode.controllable ? true : false,
    graphsEnabled,
    nodeRowCount(systemNode),
  )

  const node: Node = {
    id: systemNode.id,
    type: "asset",
    position,
    style: {
      ...style,
      width,
      height,
    },
    data: {
      label: systemNode.name,
      type: assetType,
      id: systemNode.id,
      assignmentStatus: getAssignmentStatus(systemNode),
      assignment: systemNode.assignment,
      asset: systemNode.asset
        ? {
            ...systemNode.asset,
            lifecycleStatus: toAssetStatus(systemNode.asset.status),
          }
        : systemNode.asset,
      installationId,
      attributes: (
        Array.isArray(systemNode.attributes)
          ? systemNode.attributes
          : typeof systemNode.attributes === "string"
            ? JSON.parse(systemNode.attributes)
            : undefined
      )?.map((attribute: Record<string, unknown>) => ({
        ...attribute,
        installation: installationId,
        start: installationStartDate,
        end: installationEndDate,
      })),
      operationalStatus: systemNode.operationalStatus,
      controllable: systemNode.controllable,
      container, // Pass container for dialog portal
      alerts: systemNode.alerts,
      loadPrecedence: systemNode.loadPrecedence,
    } as ApiAssetNode["data"],
  }

  // If this node belongs to an installation, set parent and extent
  if (installationId) {
    node.parentId = `group-${installationId}`
  }

  // Calculate dynamic handle positions based on connected nodes
  const handlePositions = calculateHandlePositions(
    systemNode.id,
    systemNode.position,
    connections || [],
    allNodes || [],
    nodeVersion,
    graphsEnabled,
  )

  // Only set positions if they were calculated (i.e., node has connections)
  if (handlePositions.sourcePosition !== undefined) {
    node.sourcePosition = handlePositions.sourcePosition
  }
  if (handlePositions.targetPosition !== undefined) {
    node.targetPosition = handlePositions.targetPosition
  }

  return node
}

// Create a group node for an installation
const createInstallationGroupNode = (
  installationId: string,
  installationName: string,
  installationDescription: string,
  bounds: { x: number; y: number; width: number; height: number },
  startDate: string,
  endDate?: string,
  container?: RefObject<HTMLElement | null>,
): Node => {
  return {
    id: `group-${installationId}`,
    type: "group",
    position: { x: bounds.x, y: bounds.y },
    width: bounds.width,
    height: bounds.height,
    expandParent: true,
    style: {
      backgroundColor: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      opacity: 1,
      pointerEvents: "none",
    },
    data: {
      label: installationName,
      description: installationDescription,
      installationId, // Add installation ID to data for test access
      testId: `installation-${installationId}`, // Add specific test ID
      container,
      startDate,
      endDate,
    },
    draggable: false,
    selectable: false,
  }
}

// Transform API connections to React Flow edges
export const transformConnectionsToReactFlowEdges = (
  connections: SystemViewData["connections"],
  nodes: SystemNode[],
): Edge[] => {
  const nodeStatusMap = new Map(nodes.map((node) => [node.id, getAssignmentStatus(node)]))

  return connections.map((connection) => {
    const sourceStatus = nodeStatusMap.get(connection.sourceNodeId)
    const targetStatus = nodeStatusMap.get(connection.targetNodeId)

    // Edge is animated if both source and target are active
    const animated = sourceStatus === "active" && targetStatus === "active"

    // Edge style based on connection health
    const style = animated ? { stroke: "#39aed2", strokeWidth: 2 } : { stroke: "#a9a9a9", strokeWidth: 2 }

    return {
      id: connection.id,
      source: connection.sourceNodeId,
      target: connection.targetNodeId,
      sourceHandle: connection.sourceHandle ?? undefined,
      targetHandle: connection.targetHandle ?? undefined,
      animated,
      style,
    }
  })
}

// Helper to get the center of a node for handle direction calculations
function getNodeCenter(node: SystemNode, nodeVersion: SystemNodeVersion, graphsEnabled?: boolean) {
  const { width, height } = getNodeDimensions(
    nodeVersion,
    !!(node.assignment || node.controllable),
    graphsEnabled,
    nodeRowCount(node),
  )
  return {
    x: node.position.x + width / 2,
    y: node.position.y + height / 2,
  }
}

// Helper to determine direction with a threshold for vertical vs horizontal
const THRESHOLD_RATIO = 0.4 // Tune as needed
function getDirection(deltaX: number, deltaY: number) {
  if (Math.abs(deltaY) > Math.abs(deltaX) * THRESHOLD_RATIO) {
    // Significant vertical component
    return deltaY > 0 ? Position.Bottom : Position.Top
  } else {
    // Mostly horizontal
    return deltaX > 0 ? Position.Right : Position.Left
  }
}

// Calculate dynamic handle positions based on connected nodes
const calculateHandlePositions = (
  nodeId: string,
  _nodePosition: { x: number; y: number },
  connections: SystemViewData["connections"],
  allNodes: SystemNode[],
  nodeVersion: SystemNodeVersion,
  graphsEnabled?: boolean,
) => {
  const nodeMap = new Map(allNodes.map((node) => [node.id, node]))
  const selfNode = nodeMap.get(nodeId)
  if (!selfNode) {
    return { sourcePosition: undefined, targetPosition: undefined }
  }
  const nodeCenter = getNodeCenter(selfNode, nodeVersion, graphsEnabled)

  // Find outgoing connections (where this node is source)
  const outgoingConnections = connections.filter((conn) => conn.sourceNodeId === nodeId)
  // Find incoming connections (where this node is target)
  const incomingConnections = connections.filter((conn) => conn.targetNodeId === nodeId)

  let sourcePosition: Position | undefined = undefined // Only set if connected
  let targetPosition: Position | undefined = undefined // Only set if connected

  // Calculate source position based on outgoing connections
  if (outgoingConnections.length > 0) {
    const directions: Position[] = []
    outgoingConnections.forEach((conn) => {
      const targetNode = nodeMap.get(conn.targetNodeId)
      if (!targetNode) return
      const targetCenter = getNodeCenter(targetNode, nodeVersion, graphsEnabled)
      const deltaX = targetCenter.x - nodeCenter.x
      const deltaY = targetCenter.y - nodeCenter.y
      directions.push(getDirection(deltaX, deltaY))
    })
    // Use the most common direction, or the first if tied
    if (directions.length > 0) {
      const directionCounts = directions.reduce(
        (acc, dir) => {
          acc[dir] = (acc[dir] || 0) + 1
          return acc
        },
        {} as Record<Position, number>,
      )
      sourcePosition = Object.entries(directionCounts).sort(([, a], [, b]) => b - a)[0][0] as Position
    }
  }

  // Calculate target position based on incoming connections
  if (incomingConnections.length > 0) {
    const directions: Position[] = []
    incomingConnections.forEach((conn) => {
      const sourceNode = nodeMap.get(conn.sourceNodeId)
      if (!sourceNode) return
      const sourceCenter = getNodeCenter(sourceNode, nodeVersion, graphsEnabled)
      const deltaX = sourceCenter.x - nodeCenter.x
      const deltaY = sourceCenter.y - nodeCenter.y
      directions.push(getDirection(deltaX, deltaY))
    })
    // Use the most common direction, or the first if tied
    if (directions.length > 0) {
      const directionCounts = directions.reduce(
        (acc, dir) => {
          acc[dir] = (acc[dir] || 0) + 1
          return acc
        },
        {} as Record<Position, number>,
      )
      targetPosition = Object.entries(directionCounts).sort(([, a], [, b]) => b - a)[0][0] as Position
    }
  }

  return { sourcePosition, targetPosition }
}

// Transform complete SystemView to React Flow format
/**
 * Transforms the system view data (from GetSystemView query) into ReactFlow nodes and edges.
 *
 * Note: Child assets (those with a non-null `parent` field) are excluded from the system diagram.
 * This invariant is enforced at the backend data layer — `provision_system_node()` skips creating
 * system_node entries for child assets, and `on_asset_assigned_parent()` removes any existing
 * system_node when an asset is assigned a parent. The frontend does not need additional filtering;
 * it simply renders whatever nodes the query returns.
 */
export const transformSystemViewToReactFlow = (
  systemView: SystemViewData,
  container?: RefObject<HTMLElement | null>,
  groupsEnabled?: boolean,
  graphsEnabled?: boolean,
  nodeVersion: SystemNodeVersion = SystemNodeVersion.V1,
  alertsEnabled?: boolean,
) => {
  const nodes: Node[] = []
  const allSystemNodes: SystemNode[] = []

  // First pass: collect all system nodes
  systemView.installations.forEach((installationGroup) => {
    installationGroup.nodes.forEach((node) => {
      allSystemNodes.push(node)
    })
  })

  systemView.isolatedNodes.forEach((node) => {
    allSystemNodes.push(node)
  })

  // Use DB-stored positions directly — no overlap resolution or offset logic.
  // Users can freely position any node and positions persist across page refreshes.
  const allSystemNodesResolved = allSystemNodes

  // Second pass: process installation nodes with grouping and handle positions
  systemView.installations.forEach((installationGroup) => {
    const installation = installationGroup.installation
    const installationNodes = installationGroup.nodes

    // Calculate bounds for this installation group (uses resolved positions)
    const groupBounds = calculateGroupBounds(installationNodes, nodeVersion, undefined, graphsEnabled)

    // Create the group node for this installation
    const groupNode = createInstallationGroupNode(
      installation.id,
      installation.name,
      installation.description || "",
      groupBounds,
      installation.start_date,
      installation.end_date,
      container,
    )
    if (groupsEnabled) nodes.push(groupNode)

    const installationStartDate = startOfDay(
      installation.start_date ? toUserTZ(installation.start_date) : nowDateTime(),
    )
    const installationEndDate = endOfDay(installation.end_date ? toUserTZ(installation.end_date) : nowDateTime())

    // Process nodes within this installation
    installationNodes.forEach((node) => {
      nodes.push(
        transformSystemNodeToReactFlowNode(
          node,
          groupsEnabled ? installation.id : undefined, // Only pass installationId if groups are enabled (sets parentId)
          installationStartDate,
          installationEndDate,
          groupsEnabled ? groupBounds : undefined, // Only pass groupBounds if groups are enabled
          systemView.connections,
          allSystemNodesResolved,
          container,
          nodeVersion,
          graphsEnabled,
          alertsEnabled,
        ),
      )
    })
  })

  // Process isolated nodes (no grouping)
  systemView.isolatedNodes.forEach((node) => {
    nodes.push(
      transformSystemNodeToReactFlowNode(
        node,
        undefined,
        undefined,
        undefined,
        undefined,
        systemView.connections,
        allSystemNodesResolved,
        container,
        nodeVersion,
        graphsEnabled,
      ),
    )
  })

  // Transform connections
  const edges = transformConnectionsToReactFlowEdges(systemView.connections, allSystemNodesResolved)

  return {
    nodes,
    edges,
    systemInfo: systemView.system,
    installations: systemView.installations,
    metadata: systemView.metadata,
    installation: groupsEnabled ? undefined : systemView.installations[0],
  }
}
